import axios from 'axios';
import { EventSourceParser } from "eventsource-parser";
import { ChatModel, isModelReasoning, getModelConfig, MODEL_REGISTRY } from '../constant';
import type { ChatMessage } from '../interface';

export const openai = axios.create({
    baseURL: 'https://api.openai.com/v1',
    headers: {
        'Content-Type': 'application/json'
    }
});

export type GPTChatModel = ChatModel
export type GPTChatMessageContentString = string
export type GPTChatMessageContentText = { type: 'text', text: string }
export type GPTChatMessageContentImage = { type: 'image_url', image_url: { url: string, detail?: 'auto' | 'low' | 'high' } }
export type GPTChatMessage = { role: ChatMessage['senderRole'] | 'developer', content: ChatMessageContent }
export type ChatMessageContent = GPTChatMessageContentString | (GPTChatMessageContentText | GPTChatMessageContentImage)[]
export type GPTChatMessageWithToken = GPTChatMessage & { tokens?: number }
export type GPTChatResult = {
    id: string,
    object: 'chat.completion',
    created: number,
    model: string,
    choices: {
        index: number,
        message: GPTChatMessage,
        finish_reason: string,
        delta?: {
            content?: string,
            role?: string,
        }
    }[],
    usage: {
        prompt_tokens: number,
        completion_tokens: number,
        total_tokens: number,
        // For reasoning models
        completion_tokens_details?: {
            reasoning_tokens?: number,
            accepted_prediction_tokens?: number,
            rejected_prediction_tokens?: number
        }
    }
}
/** Extra metadata passed alongside streaming text (e.g. reasoning/thinking content) */
export interface OnMessageExtras {
    /** Accumulated reasoning/thinking text from reasoning models */
    reasoning?: string;
    /** Whether the model is still in the thinking phase */
    isThinking?: boolean;
    /** Provider-reported usage; sent with the final (lastCall) callback only. */
    usage?: { inputTokens: number; outputTokens: number; reasoningTokens?: number };
}

export type OnMessageCallback = (text: string, firstCall?: boolean, lastCall?: boolean, extras?: OnMessageExtras) => void

/**
 * Prepares messages for OpenAI API based on model type
 * - For reasoning models (o1, o3, o4): converts system messages to developer role
 * - For standard models: keeps messages as-is
 */
const prepareMessagesForModel = (messages: GPTChatMessage[], model: GPTChatModel): GPTChatMessage[] => {
    const isReasoning = isModelReasoning(model);

    if (!isReasoning) {
        return messages;
    }

    // For reasoning models, convert system messages to developer role
    return messages.map(msg => {
        if (msg.role === 'system') {
            return {
                ...msg,
                role: 'developer' as const,
            };
        }
        return msg;
    });
};

/**
 * Gets the max tokens for a model, with special handling for reasoning models
 */
const getMaxTokensForModel = (model: GPTChatModel): number | undefined => {
    const config = getModelConfig(model);
    if (!config) return undefined;

    // For reasoning models, we may want to limit output or let the API decide
    if (config.isReasoning) {
        // Reasoning models can output up to 100k tokens, but we'll let the API decide
        return undefined;
    }

    return Math.min(config.maxOutputTokens, 4096);
};

export const openAiChatRequest = async (
    apiKey: string,
    model: GPTChatModel,
    messages: GPTChatMessage[],
    signal?: AbortController,
    options?: {
        temperature?: number;
        maxTokens?: number;
        responseFormat?: { type: 'text' | 'json_object' };
    }
) => {
    const modifiedMessages = prepareMessagesForModel(messages, model);
    const isReasoning = isModelReasoning(model);

    const requestBody: Record<string, any> = {
        model,
        messages: modifiedMessages,
    };

    // Add optional parameters (not all apply to reasoning models)
    if (!isReasoning) {
        if (options?.temperature !== undefined) {
            requestBody.temperature = options.temperature;
        }
        if (options?.maxTokens !== undefined) {
            requestBody.max_tokens = options.maxTokens;
        }
        if (options?.responseFormat) {
            requestBody.response_format = options.responseFormat;
        }
    }

    const response = await openai.post<GPTChatResult>(
        '/chat/completions',
        requestBody,
        {
            signal: signal?.signal,
            headers: {
                'Authorization': `Bearer ${apiKey}`,
            },
        }
    );
    return response.data;
};

export type OpenAiChatRequestHandlerExtra = {
    onMessage?: OnMessageCallback;
    useStreaming?: true;
    temperature?: number;
    maxTokens?: number;
    system?: string;
    responseFormat?: { type: 'text' | 'json_object' };
};

export const openAiChatRequestHandler = async <T extends OpenAiChatRequestHandlerExtra | undefined>(
    apiKey: string,
    model: GPTChatModel,
    messages: GPTChatMessage[],
    signal?: AbortController,
    extra?: T
): Promise<T extends { useStreaming: true } ? null : GPTChatResult> => {
    try {
        const isReasoning = isModelReasoning(model);

        // Reasoning models have limited streaming support - check model capabilities
        const canStream = !isReasoning || model === 'o1' || model.startsWith('o3') || model.startsWith('o4');

        if (extra?.useStreaming && extra?.onMessage && canStream) {
            await openAiChatRequestStream(apiKey, model, messages, extra.onMessage, signal, {
                temperature: extra.temperature,
                maxTokens: extra.maxTokens,
            });
            return null as any;
        } else {
            const request = await openAiChatRequest(apiKey, model, messages, signal, {
                temperature: extra?.temperature,
                maxTokens: extra?.maxTokens,
                responseFormat: extra?.responseFormat,
            });
            return request as any;
        }
    } catch (error: any) {
        let message =
            error?.response?.data?.error?.message || error?.message || 'There are some error';
        if (message.startsWith('Error: ')) {
            message = message.replace('Error: ', '');
        }
        const isModelNotAvailable =
            message.startsWith('The model:') && message.endsWith('does not exist');
        if (isModelNotAvailable) {
            message = `${message}, make sure you have access or allowed to use this model.`;
        }
        // Handle rate limits
        if (error?.response?.status === 429) {
            message = 'Rate limit exceeded. Please wait a moment and try again.';
        }
        // Handle context length errors
        if (message.includes('context_length_exceeded') || message.includes('maximum context length')) {
            const config = getModelConfig(model);
            message = `Message too long for ${config?.displayName || model}. Maximum context: ${config?.contextWindow?.toLocaleString() || 'unknown'} tokens.`;
        }
        throw new Error(message);
    }
};

export const openAiChatRequestStream = async (
    apiKey: string,
    model: GPTChatModel,
    messages: GPTChatMessage[],
    onMessage: OnMessageCallback,
    signal?: AbortController,
    options?: {
        temperature?: number;
        maxTokens?: number;
    }
) => {
    const apiUrl = "https://api.openai.com/v1/chat/completions";
    const modifiedMessages = prepareMessagesForModel(messages, model);
    const isReasoning = isModelReasoning(model);

    const requestBody: Record<string, any> = {
        model,
        messages: modifiedMessages,
        stream: true,
    };

    // Add stream options for newer models
    requestBody.stream_options = { include_usage: true };

    // Add optional parameters (not all apply to reasoning models)
    if (!isReasoning) {
        if (options?.temperature !== undefined) {
            requestBody.temperature = options.temperature;
        }
        if (options?.maxTokens !== undefined) {
            requestBody.max_tokens = options.maxTokens;
        }
    }

    const requestOptions = {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify(requestBody),
        signal: signal?.signal,
    };

    try {
        const response = await fetch(apiUrl, requestOptions);

        if (response.ok) {
            const reader = response.body!.getReader();
            const decoder = new TextDecoder("utf-8");
            let partialMessage = "";
            let firstCall = true;
            let fullContent = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                const lines = chunk.split("\n");

                for (const line of lines) {
                    const trimmedLine = line.trim();

                    if (trimmedLine === "") continue;
                    if (trimmedLine === "data: [DONE]") {
                        onMessage('', false, true);
                        return null;
                    }

                    if (trimmedLine.startsWith("data: ")) {
                        try {
                            const jsonStr = trimmedLine.slice(6);
                            const parsed = JSON.parse(jsonStr);
                            const content = parsed.choices?.[0]?.delta?.content;

                            if (content) {
                                fullContent += content;
                                onMessage(fullContent, firstCall);
                                firstCall = false;
                            }
                        } catch (e) {
                            // Partial JSON, accumulate
                            partialMessage += trimmedLine;
                        }
                    }
                }
            }
            onMessage('', false, true);
            return null;
        } else {
            const data = await response.json();
            throw new Error(`Error: ${data.error?.message || 'Unknown error'}`);
        }
    } catch (error) {
        console.error("Error fetching OpenAI API:", error);
        throw error;
    }
};

export const openAiTranscribeRequest = async (
    apiKey: string,
    audioBlob: Blob,
    options?: {
        language?: string;
        prompt?: string;
    }
): Promise<string> => {
    const formData = new FormData();
    formData.append('file', audioBlob);
    formData.append('model', 'whisper-1');

    if (options?.language) {
        formData.append('language', options.language);
    }
    if (options?.prompt) {
        formData.append('prompt', options.prompt);
    }

    const response = await axios.post(
        'https://api.openai.com/v1/audio/transcriptions',
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: `Bearer ${apiKey}`,
            },
        }
    );

    return response.data.text;
};

// Note: DALL-E image generation removed - use AI SDK tools for image generation instead

/**
 * Text-to-Speech using OpenAI's TTS API
 */
export type TTSVoice = 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer';
export type TTSModel = 'tts-1' | 'tts-1-hd';

export const openAiTextToSpeechRequest = async (
    apiKey: string,
    input: string,
    options?: {
        model?: TTSModel;
        voice?: TTSVoice;
        speed?: number; // 0.25 to 4.0
    }
): Promise<ArrayBuffer> => {
    const response = await axios.post(
        'https://api.openai.com/v1/audio/speech',
        {
            model: options?.model || 'tts-1',
            input,
            voice: options?.voice || 'alloy',
            speed: options?.speed || 1.0,
        },
        {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${apiKey}`,
            },
            responseType: 'arraybuffer',
        }
    );

    return response.data;
};

/**
 * Embeddings using OpenAI's embedding API
 */
export type EmbeddingModel = 'text-embedding-3-small' | 'text-embedding-3-large' | 'text-embedding-ada-002';

export const openAiEmbeddingRequest = async (
    apiKey: string,
    input: string | string[],
    options?: {
        model?: EmbeddingModel;
        dimensions?: number;
    }
): Promise<{ embedding: number[]; index: number }[]> => {
    const response = await axios.post<{
        data: { embedding: number[]; index: number }[];
        usage: { prompt_tokens: number; total_tokens: number };
    }>(
        'https://api.openai.com/v1/embeddings',
        {
            model: options?.model || 'text-embedding-3-small',
            input,
            ...(options?.dimensions && { dimensions: options.dimensions }),
        },
        {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${apiKey}`,
            },
        }
    );

    return response.data.data;
};

